import { formatMoney, toMoney } from '@betterment/core';
import { Box, Button, Icon, ListRow, Text, VStack } from '@betterment/samba-react';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';

import { ExistingPurposesQuery } from '../../graphql/query.existingPurposes.generated';

type Purpose = ExistingPurposesQuery['purposes'][0];

const accountDescriptionLine = (purpose: Purpose): string => {
  switch (purpose.__typename) {
    case 'PurposeEmergencyFund': {
      const fundTarget = purpose.emergencyFundTarget?.amount?.cents;
      return fundTarget != null && fundTarget > 0 ? `Savings target: ${formatMoney(toMoney(fundTarget))}` : '';
    }
    case 'PurposeMajorPurchase': {
      const fundTarget = purpose.majorPurchaseTarget?.amount?.cents;
      return fundTarget != null && fundTarget > 0 ? `Savings target: ${formatMoney(toMoney(fundTarget))}` : '';
    }
    case 'PurposeRetirementGrowth': {
      const retirementAge = purpose.financialProfile.retirementAssumptions?.userAssumptions.retirementAge;
      return retirementAge != null ? `Desired retirement age: ${retirementAge}` : '';
    }
    default:
      return '';
  }
};

type AssignToExistingPurposeProps = {
  purpose: Purpose;
  handleAssignment: (purposeId: string) => void;
};

export const AssignToExistingPurpose: React.FC<AssignToExistingPurposeProps> = ({ purpose, handleAssignment }) => {
  return (
    <Button
      onClick={() => handleAssignment(purpose.id)}
      variant="ghostSecondary"
      spacing="compact"
      css={{
        px: 0,
        py: 0,
        borderStyle: 'solid',
        borderWidth: 'thin',
        borderColor: 'border.weak',
        borderRadius: 'medium',
      }}
    >
      <ListRow css={{ w: '100%' }}>
        <ListRow.LeadingAsset>
          <Box css={{ w: 'size.450', h: 'size.450' }}>
            <ThemedImage themedAsset={purpose.image} alt={purpose.name} />
          </Box>
        </ListRow.LeadingAsset>
        <ListRow.LeadingContent>
          <VStack>
            <Text>{purpose.name}</Text>
            <Text color="text.weak" variant="text50">
              {accountDescriptionLine(purpose)}
            </Text>
          </VStack>
        </ListRow.LeadingContent>
        <ListRow.TrailingAsset>
          <Icon name="CaretRight" size="small" />
        </ListRow.TrailingAsset>
      </ListRow>
    </Button>
  );
};
